/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCallWithJobSwaggers, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import {
    AutoTaggingCreationSwagger,
    AutoTaggingJobListSwagger,
    AutoTaggingJobSwagger
} from 'src/customText/core/services/autoTagging/customTextAutoTaggingSwagger';
import { CustomTextExportedProjectDocumentSwagger } from 'src/customText/core/services/projects/customTextProjectSwaggers';

export const getCustomTextAutoTaggingService = createService(({ context, getEnvironment }) => {
    const { isProduction, webApiUrl } = getEnvironment({ apiName: 'customText' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlBuilder = (projectName?: string) =>
        urlBuilder()
            .baseUrl(webApiUrl)
            .resource('internal')
            .project(projectName)
            .queryParam('api-version', LuisConstants.CustomText.InternalApiVersion);

    const initiateAutoTaggingJob = async ({
        projectName,
        documents,
        trainedModelLabel,
        extractors
    }: {
        projectName: string;
        documents: CustomTextExportedProjectDocumentSwagger[];
        trainedModelLabel: string;
        extractors: string[];
    }): Promise<string> => {
        const url = getUrlBuilder(projectName)
            .resource(':autotag')
            .build();

        // This is a required header for the backend in case of dev env to get it working without APIM layer
        const modifiedApiOptions = isProduction
            ? apiOptions
            : luisRequestOptionsBuilder()
                  .useDefaultOptions(context.serviceKey, isProduction)
                  .useCustomHeader('apim-product-id', '123')
                  .useCustomHeader('Azure-Resource-Id-Enc', '12341234')
                  .build();

        const payload: AutoTaggingCreationSwagger = { documents, autoTagParameters: { trainedModelLabel, extractors } };

        const { headers } = await httpService.post<void>(url, payload, modifiedApiOptions);

        return (<Record<string, string>>headers)[LuisConstants.OperationHeaders.CTLocationHeader];
    };

    const pollAutoTagging = async ({
        statusUrl,
        jobId,
        projectName,
        operationHandler
    }: {
        statusUrl?: string;
        jobId?: string;
        projectName?: string;
        operationHandler: OperationHandler<AutoTaggingJobSwagger>;
    }): Promise<void> => {
        if (!statusUrl) {
            statusUrl = getUrlBuilder(projectName)
                .resource('autotag')
                .resource('jobs', jobId)
                .build();
        }

        await createOperationCallWithJobSwaggers(statusUrl, apiOptions, operationHandler);
    };

    const getAutoTaggingJobs = async ({
        projectName,
        resourcePath,
        nextPageLink
    }: {
        projectName: string;
        resourcePath: string;
        nextPageLink?: string;
    }): Promise<AutoTaggingJobListSwagger> => {
        const url =
            nextPageLink ??
            getUrlBuilder(projectName)
                .resource('autotag')
                .resource('jobs')
                .build();

        // This is a required header for the backend in case of dev env to get it working without APIM layer
        const modifiedApiOptions = isProduction
            ? apiOptions
            : luisRequestOptionsBuilder()
                  .useDefaultOptions(context.serviceKey, isProduction)
                  .useCustomHeader('Azure-Resource-Id', resourcePath)
                  .build();

        const { data } = await httpService.get<AutoTaggingJobListSwagger>(url, modifiedApiOptions);

        return data;
    };

    return { initiateAutoTaggingJob, pollAutoTagging, getAutoTaggingJobs };
});
